<template>
  <!-- Just an example -->
  <header
    class="c-site-header"
    :class="{
      'c-site-header--menu-open': menuIsOpen,
    }"
  >
    <div class="mx-auto >=760:px-layout-margin w-full max-w-layout-max">
      <SvgColorBand class="w-full" />

      <nav
        class="flex gap-x-[20px] items-center min-h-[55px] >=760:min-h-[80px] px-layout-margin >=760:px-[0px] py-[15px] >=760:py-[20px]"
      >
        <template v-if="site">
          <!-- Home link -->
          <NuxtLink
            v-if="site?.url"
            :to="site?.url"
            :aria-label="siteName ? `Til ${siteName} forsiden` : 'Til forsiden'"
            class="flex-shrink-0"
          >
            <SvgHeaderLogo class="w-auto h-[30px] >=760:h-[40px]" />
          </NuxtLink>

          <!-- Items -->
          <div
            v-if="site.navigation"
            class="hidden >=760:flex flex-col w-full >=990:contents"
          >
            <!-- Primary -->
            <ul
              v-if="computedNavigationPrimary?.length"
              class="order-2 >=990:order-none flex gap-x-[25px] items-center w-full >=990:w-fit"
            >
              <li
                v-for="(item, index) in computedNavigationPrimary"
                :key="item.id + '-' + index"
              >
                <NuxtLink
                  class="text-22 transition-color duration-300 hover:text-orange"
                  :class="{
                    'text-orange': route.path.includes(item.url),
                  }"
                  :to="item.url"
                >
                  <span>{{ item.name }}</span>
                </NuxtLink>
              </li>
            </ul>

            <!-- Secondary -->
            <ul
              v-if="computedNavigationSecondary?.length"
              class="order-1 >=990:order-none flex gap-x-[15px] items-center w-full >=990:w-fit >=990:ml-auto"
            >
              <li
                v-for="(item, index) in computedNavigationSecondary"
                :key="item.id + '-' + index"
              >
                <NuxtLink
                  v-if="!item.isMenu"
                  class="text-12 transition-color duration-300 hover:text-orange"
                  :class="{
                    'text-orange': route.path.includes(item.url),
                    'group-hover:text-white': item.isMenu,
                  }"
                  :to="item.url"
                  @click.shift="handleEnvLogin($event, item)"
                >
                  <span class="flex">{{ item.name }}</span>
                </NuxtLink>

                <!-- Menu button -->
                <div
                  v-else
                  class="group isolate relative text-12 text-text hover:text-white hover:font-bold focus-within:text-white focus-within:font-bold transition-all duration-150"
                >
                  <!-- Button -->
                  <span class="relative inline-flex items-center z-10">
                    <span class="inline-flex justify-end items-center">
                      <span class="absolute">
                        {{ item.name }}
                      </span>
                      <span class="font-bold invisible">
                        {{ item.name }}
                      </span>
                    </span>
                    <SvgIconWedge
                      class="w-[8px] h-[8px] rotate-90 ml-[8px] group-hover:rotate-270 focus-within:rotate-270 transition-transform duration-150"
                    />
                  </span>

                  <!-- Menu -->
                  <div
                    class="c-site-header__profile-menu absolute top-[0px] right-[0px] w-full min-w-max pt-[calc(1.25em+10px)] flex flex-col items-center opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto font-normal transition-opacity duration-150"
                  >
                    <div
                      class="flex flex-col w-[calc(100%+20px)] p-[10px] bg-orange-dark"
                    >
                      <template v-if="isSupplier">
                        <NuxtLink
                          class="c-site-header-link__white text-16 pt-[5px] w-fit"
                          to="/leverandoer/"
                        >
                          Leverandør
                        </NuxtLink>
                        <NuxtLink
                          class="c-site-header-link__white text-16 pt-[5px] w-fit"
                          to="/leverandoer/produkter/"
                        >
                          Produkter
                        </NuxtLink>
                      </template>
                      <NuxtLink
                        class="c-site-header-link__white text-16 pt-[5px] w-fit"
                        to="/skift-password/"
                      >
                        Skift password
                      </NuxtLink>
                      <NuxtLink
                        class="c-site-header-link__white text-16 pt-[5px] mb-[5px] w-fit"
                        to="/log-ud/"
                      >
                        Log ud
                      </NuxtLink>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>

          <!-- Menu -->
          <button
            :aria-label="menuIsOpen ? 'Luk menu' : 'Åben menu'"
            class=">=760:hidden ml-auto w-auto h-auto"
            @click="toggleMenu"
          >
            <SvgIconBurger class="c-site-header__burger-icon w-auto h-[23px]" />
          </button>
        </template>
      </nav>

      <!-- Burger menu -->
      <HeightWrapper
        class="absolute left-0 w-full >=760:hidden bg-white transition-all duration-500 overflow-hidden"
        :style="
          menuIsOpen && {
            boxShadow: '0 15px 15px rgba(0,0,0,0.2)',
          }
        "
      >
        <template v-if="site">
          <div
            class="transition-opacity duration-500"
            :class="{
              'max-h-[0px] opacity-0': !menuIsOpen,
            }"
            @focusin.capture="toggleMenu(true)"
          >
            <ul
              v-if="
                computedNavigationPrimary?.length ||
                site.navigation.secondary?.length
              "
              class="w-full px-layout-margin py-[5px]"
            >
              <template v-if="computedNavigationPrimary?.length">
                <li
                  v-for="(item, index) in computedNavigationPrimary"
                  :key="item.id + '-' + index"
                  :class="{
                    'border-t border-gray': index,
                  }"
                >
                  <NuxtLink
                    class="block w-full py-[12px] text-25 transition-color duration-300 hover:text-orange"
                    :class="{
                      'text-orange': route.path.includes(item.url),
                    }"
                    :to="item.url"
                  >
                    <span>{{ item.name }}</span>
                  </NuxtLink>
                </li>
              </template>
              <template v-if="computedNavigationSecondaryMobile?.length">
                <li
                  v-for="(item, index) in computedNavigationSecondaryMobile"
                  :key="item.id + '-' + index"
                  :class="{
                    'border-t border-gray': index,
                  }"
                >
                  <NuxtLink
                    class="block w-full py-[12px] text-18 transition-color duration-300 hover:text-orange"
                    :class="{
                      'text-orange': route.path.includes(item.url),
                    }"
                    :to="item.url"
                  >
                    <span>{{ item.name }}</span>
                  </NuxtLink>
                </li>
              </template>
            </ul>
          </div>
        </template>
      </HeightWrapper>
    </div>
  </header>
</template>

<script setup>
import { storeToRefs } from "pinia";
import SvgColorBand from "./../assets/svgs/color-band.svg";
import SvgHeaderLogo from "~/assets/svgs/header-logo.svg";
import SvgIconBurger from "./../assets/svgs/icon-burger.svg";
import SvgIconWedge from "./../assets/svgs/icon-wedge.svg";
import { useMainStore } from "~/store/main";

defineProps({
  siteName: String,
});

const main = useMainStore();
const { site } = storeToRefs(main);

const hasReachedClient = ref(true);
const isLoggedIn = computed(() => {
  return (hasReachedClient.value && main.isLoggedIn) || false;
});
const isSupplier = computed(() => {
  return (
    main.solution === "mba" && main.loginData?.roles?.includes?.(".Leverandør")
  );
});
onMounted(() => {
  hasReachedClient.value = true;
});

const menuIsOpen = ref(false);
const toggleMenu = (value = !menuIsOpen.value) => {
  if (typeof value !== "boolean") {
    value = !menuIsOpen.value;
  }

  menuIsOpen.value = value;
};

const computedNavigationPrimary = computed(() => {
  if (isLoggedIn.value && main.solution === "mba") {
    const updatedNav = [...(main.site?.navigation?.primary || [])];
    updatedNav.splice(1, 0, {
      id: 9999,
      name: "Mine sager",
      url: "/virksomhed/mine-sager/",
    });
    return updatedNav;
  }
  return main.site?.navigation?.primary || [];
});
const computedNavigationSecondary = computed(() => {
  if (isLoggedIn.value) {
    const updatedNav =
      main.solution === "mba"
        ? [
            {
              id: 2811,
              name: "Find via behandlingsnummer",
              url: "/find-via-behandlingsnummer/",
            },
            { id: 2806, name: "Min profil", isMenu: true },
          ]
        : [{ id: 2806, name: "Min profil", isMenu: true }];
    return updatedNav;
  } else if (main.solution === "mks") {
    return [
      {
        id: 2758,
        name: "Log ind",
        url: "/log-ind/",
      },
    ];
  }
  return main.site?.navigation?.secondary || [];
});
const computedNavigationSecondaryMobile = computed(() => {
  if (isLoggedIn.value) {
    const updatedNav =
      main.solution === "mba"
        ? [
            {
              id: 2811,
              name: "Find via behandlingsnummer",
              url: "/find-via-behandlingsnummer/",
            },
            {
              id: 2812,
              name: "Skift password",
              url: "/skift-password/",
            },
            {
              id: 2813,
              name: "Log ud",
              url: "/log-ud/",
            },
          ]
        : [
            {
              id: 2761,
              name: "Skift password",
              url: "/skift-password/",
            },
            {
              id: 2762,
              name: "Log ud",
              url: "/log-ud/",
            },
          ];
    return updatedNav;
  } else if (main.solution === "mks") {
    return [
      {
        id: 2758,
        name: "Log ind",
        url: "/log-ind/",
      },
    ];
  }
  return main.site?.navigation?.secondary || [];
});

const route = useRoute();
watch(route, () => {
  menuIsOpen.value = false;
});

function handleEnvLogin(e, item) {
  if (+item.id === 2758) {
    const config = useRuntimeConfig();
    if (config.public?.login?.user && config.public?.login?.password) {
      e.preventDefault();

      $fetch("/api/login/validate", {
        method: "POST",
        body: {
          username: config.public.login.user,
          password: config.public.login.password,
          rememberme: true,
        },
      })
        .then(() => {
          location.href = location.href;
        })
        .catch((error) => {});
    }
  }
}
</script>

<style lang="postcss">
.c-site-header {
  @apply isolate relative bg-white text-text z-10;

  .orange-background-gradient {
    background-image: linear-gradient(#dc6a1e, #fa802f);
  }
}
.c-site-header-link__white {
  border-bottom: 1px solid rgba(255, 255, 255, 0.4);
  transition: color 0.3s, border 0.3s;
  color: white;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
}

.c-site-header__profile-menu:before {
  @apply absolute block left-[-10px] top-[-10px] w-[calc(100%+20px)] h-[calc(1.25em+20px)] bg-orange -z-1;
  content: "";
}

/* Burger menu icon animation */
.c-site-header__burger-icon {
  @apply overflow-visible;
}
.c-site-header__burger-icon path {
  transition: transform 0.5s, opacity 0.5s;
  transform-origin: center;
}

.c-site-header--menu-open .c-site-header__burger-icon path:last-child {
  transform: rotate(90deg) rotate(45deg) translateY(50%) translateY(-1px);
}
.c-site-header--menu-open .c-site-header__burger-icon path:nth-child(2) {
  transform: translateX(-1px) rotate(-90deg) rotate(-45deg);
}
.c-site-header--menu-open .c-site-header__burger-icon path:first-child {
  transform: translateY(40%);
  opacity: 0;
}
</style>
